import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, User, Calendar, Share2, ArrowRight } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogDetailPage = () => {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${BACKEND_URL}/api/blog/${articleId}`).then(r => r.ok ? r.json() : null),
      fetch(`${BACKEND_URL}/api/blog`).then(r => r.json()),
    ]).then(([art, all]) => {
      setArticle(art);
      setAllArticles(all.filter(a => a.id !== articleId).slice(0, 3));
      if (art) trackPageView(`/blog/${articleId}`, art.title);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [articleId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-coral border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl font-bold text-white mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-coral hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: article.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-void" data-testid="blog-detail-page">
      {article && (
        <SEOHead
          title={article.title}
          description={article.excerpt}
          path={`/blog/${articleId}`}
          ogType="article"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "author": { "@type": "Organization", "name": article.author || "Avi Cement Technical Team" },
            "publisher": { "@type": "Organization", "name": "Avi Cement Limited" },
            "datePublished": article.date,
            "url": `https://avicement.com/blog/${articleId}`,
          }}
        />
      )}
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-15" />
        <div className="container mx-auto px-4 md:px-8 max-w-4xl relative z-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-coral font-mono text-sm mb-4 block">{article.category}</span>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">{article.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm mb-8">
              <span className="flex items-center gap-1"><User className="w-4 h-4" /> {article.author}</span>
              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date(article.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {article.read_time}</span>
              <button onClick={handleShare} className="flex items-center gap-1 text-coral hover:text-white transition-colors ml-auto" data-testid="share-btn">
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="pb-8">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="rounded-2xl overflow-hidden aspect-video">
            <img src={article.image} alt={`${article.title} - Avi Cement Blog`} loading="lazy" width="800" height="450" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-16">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="bg-surface border border-white/5 rounded-2xl p-8 md:p-12">
            <div className="prose prose-invert prose-sm max-w-none blog-content" data-testid="blog-content">
              {article.content.split('\n').map((line, i) => {
                const trimmed = line.trim();
                if (!trimmed) return <br key={i} />;
                if (trimmed.startsWith('## ')) return <h2 key={i} className="font-display text-2xl font-bold text-white mt-8 mb-4">{trimmed.slice(3)}</h2>;
                if (trimmed.startsWith('### ')) return <h3 key={i} className="font-display text-xl font-semibold text-white mt-6 mb-3">{trimmed.slice(4)}</h3>;
                if (trimmed.startsWith('| ')) {
                  // Simple table row
                  const cells = trimmed.split('|').filter(c => c.trim());
                  const isHeader = cells.some(c => c.includes('---'));
                  if (isHeader) return null;
                  return (
                    <div key={i} className="grid grid-cols-4 gap-2 text-sm border-b border-white/5 py-2">
                      {cells.map((cell, j) => (
                        <span key={j} className={j === 0 ? 'text-white font-medium' : 'text-gray-400'}>{cell.trim()}</span>
                      ))}
                    </div>
                  );
                }
                if (trimmed.startsWith('- ')) {
                  const content = trimmed.slice(2);
                  const boldMatch = content.match(/^\*\*(.*?)\*\*(.*)/);
                  if (boldMatch) {
                    return <li key={i} className="text-gray-300 text-sm ml-4 mb-1 list-disc"><span className="text-white font-medium">{boldMatch[1]}</span>{boldMatch[2]}</li>;
                  }
                  return <li key={i} className="text-gray-300 text-sm ml-4 mb-1 list-disc">{content}</li>;
                }
                if (trimmed.match(/^\d+\.\s/)) {
                  const content = trimmed.replace(/^\d+\.\s/, '');
                  return <li key={i} className="text-gray-300 text-sm ml-4 mb-1 list-decimal">{content}</li>;
                }
                // Handle **bold** inline
                const parts = trimmed.split(/(\*\*.*?\*\*)/g);
                return (
                  <p key={i} className="text-gray-300 text-sm leading-relaxed mb-3">
                    {parts.map((part, j) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <span key={j} className="text-white font-medium">{part.slice(2, -2)}</span>;
                      }
                      return part;
                    })}
                  </p>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {allArticles.length > 0 && (
        <section className="pb-20 md:pb-32 border-t border-white/5 pt-16">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <h2 className="font-display text-2xl font-bold text-white mb-8">More Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {allArticles.map((a) => (
                <Link key={a.id} to={`/blog/${a.id}`} data-testid={`related-${a.id}`} className="group block bg-surface border border-white/5 hover:border-white/20 rounded-2xl overflow-hidden transition-all card-lift">
                  <div className="aspect-video overflow-hidden">
                    <img src={a.image} alt={`${a.title} - Avi Cement Blog`} loading="lazy" width="400" height="225" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <span className="text-xs font-mono text-coral">{a.category}</span>
                    <h3 className="font-display text-base font-semibold text-white mt-1 group-hover:text-coral transition-colors">{a.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetailPage;
